import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useSafeBack } from "../hooks/useSafeBack";
import { motion } from "framer-motion";
import { ArrowLeft, Loader2, MessageCircle, Video, Briefcase, MapPin, Cake, Sparkles, Linkedin, Instagram, Music2, Home, Clock, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError, fileUrl } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useCall } from "../context/CallContext";
import { BottomSheet } from "../components/BottomSheet";
import { TimeCapsule } from "../components/TimeCapsule";

export default function PublicProfile({ ancestor }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const goBack = useSafeBack("/lineage");
  const { family } = useAuth();
  const { startCall } = useCall();
  const [p, setP] = useState(null);
  const [err, setErr] = useState("");
  const [showCapsule, setShowCapsule] = useState(false);
  const canEdit = ["admin", "organizer"].includes(family?.role);

  const removeFromTree = async () => {
    const nodeId = ancestor ? id : p?.tree_node_id;
    if (!nodeId) return;
    if (!window.confirm(`Remove ${p?.name || "this person"} from the family tree? This can't be undone.`)) return;
    try {
      await api.delete(`/lineage/nodes/${nodeId}`);
      toast.success("Removed from family tree");
      navigate("/lineage");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  useEffect(() => {
    document.body.classList.add("hide-bottom-nav");
    (async () => {
      try {
        const url = ancestor ? `/ancestor/public/${id}` : `/profile/public/${id}`;
        const { data } = await api.get(url);
        setP(data);
      } catch (e) { setErr(formatApiError(e.response?.data?.detail)); }
    })();
    return () => document.body.classList.remove("hide-bottom-nav");
  }, [id, ancestor]);

  if (err) return <div className="p-8 text-center" style={{ color: "var(--text-muted)" }}>{err}</div>;
  if (!p) return <div className="grid place-items-center" style={{ minHeight: "60vh" }}><Loader2 className="animate-spin gold-text" /></div>;

  const socials = p.socials || {};
  const socialUrl = (platform, value) => {
    if (!value) return null;
    const v = String(value).trim();
    if (/^https?:\/\//i.test(v)) return v;
    const handle = v.replace(/^@/, "").replace(/\/+$/, "");
    if (platform === "linkedin") return `https://www.linkedin.com/in/${handle}`;
    if (platform === "instagram") return `https://www.instagram.com/${handle}`;
    if (platform === "tiktok") return `https://www.tiktok.com/@${handle}`;
    return v;
  };
  const Row = ({ icon: Icon, label, value, href, testid }) => value ? (
    href ? (
      <a href={href} target="_blank" rel="noopener noreferrer" data-testid={testid}
        className="flex items-center gap-3 py-2.5" style={{ borderBottom: "1px solid var(--hairline)" }}>
        <Icon size={17} className="gold-text" />
        <div><div className="text-[11px] uppercase tracking-wide" style={{ color: "var(--text-muted)" }}>{label}</div>
          <div className="text-sm gold-text underline" style={{ wordBreak: "break-all" }}>{value}</div></div>
      </a>
    ) : (
      <div className="flex items-center gap-3 py-2.5" style={{ borderBottom: "1px solid var(--hairline)" }}>
        <Icon size={17} className="gold-text" />
        <div><div className="text-[11px] uppercase tracking-wide" style={{ color: "var(--text-muted)" }}>{label}</div>
          <div className="text-sm" style={{ color: "var(--text-primary)" }}>{value}</div></div>
      </div>
    )
  ) : null;

  return (
    <div style={{ minHeight: "100dvh" }}>
      <div className="sticky top-0 z-40 glass hairline px-4 py-3 flex items-center gap-3">
        <button onClick={goBack} data-testid="public-back-btn"><ArrowLeft size={22} /></button>
        <h1 className="font-serif-display text-2xl">Profile</h1>
        {ancestor && canEdit && (
          <button onClick={() => navigate(`/lineage?edit=${id}`)} data-testid="edit-ancestor-btn"
            className="ml-auto flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-sm font-medium gold-border"
            style={{ color: "var(--text-primary)" }}>
            <Pencil size={14} className="gold-text" /> Edit
          </button>
        )}
      </div>
      <div className="px-6 py-6">
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="flex flex-col items-center text-center">
          <div className="h-28 w-28 rounded-full overflow-hidden gold-border grid place-items-center" style={{ background: "var(--bg-elevated)" }}>
            {p.photo ? <img src={fileUrl(p.photo)} alt="" className="h-full w-full object-cover" /> : <span className="font-serif-display text-4xl gold-text">{(p.name || "?").charAt(0)}</span>}
          </div>
          <div className="font-serif-display text-3xl mt-3">{p.name}</div>
          {p.occupation && <div className="text-sm" style={{ color: "var(--text-secondary)" }}>{p.occupation}</div>}
          {ancestor && <span className="mt-2 text-[10px] uppercase tracking-widest rounded-full px-2 py-1 gold-text" style={{ background: "color-mix(in srgb, var(--accent) 14%, transparent)" }}>In Memoriam</span>}
        </motion.div>

        {!ancestor && !p.is_me && (
          <div className="flex gap-3 mt-5">
            <button onClick={() => navigate(`/messages/${p.id}`)} data-testid="chat-btn"
              className="flex-1 rounded-xl py-3 text-sm font-semibold flex items-center justify-center gap-2" style={{ background: "var(--accent)", color: "#fff" }}>
              <MessageCircle size={17} /> Chat
            </button>
            <button onClick={() => startCall(p.id, p.name)} data-testid="video-btn"
              className="flex-1 rounded-xl py-3 text-sm font-semibold flex items-center justify-center gap-2 gold-border" style={{ background: "var(--overlay)", color: "var(--text-primary)" }}>
              <Video size={17} className="gold-text" /> Video Call
            </button>
          </div>
        )}

        {!ancestor && (
          <button onClick={() => setShowCapsule(true)} data-testid="view-capsule-btn"
            className="w-full mt-3 rounded-xl py-3 text-sm font-semibold flex items-center justify-center gap-2 card-surface">
            <Clock size={16} className="gold-text" /> {p.is_me ? "My Time Capsule" : "View Time Capsule"}
          </button>
        )}

        {p.bio && <p className="text-sm mt-5" style={{ color: "var(--text-secondary)" }}>{p.bio}</p>}

        <div className="mt-4">
          {!ancestor && p.age != null && <Row icon={Cake} label="Age" value={`${p.age} years`} />}
          {ancestor && (p.birth_date || p.death_date) && <Row icon={Cake} label="Lived" value={[p.birth_date, p.death_date].filter(Boolean).join(" – ")} />}
          <Row icon={MapPin} label={ancestor ? "Birth city" : "Birth city"} value={p.birth_city} />
          {!ancestor && <Row icon={Home} label="Current city" value={p.current_city} />}
          <Row icon={Sparkles} label="Hobbies" value={p.hobbies} />
          <Row icon={Linkedin} label="LinkedIn" value={socials.linkedin} href={socialUrl("linkedin", socials.linkedin)} testid="social-linkedin" />
          <Row icon={Instagram} label="Instagram" value={socials.instagram} href={socialUrl("instagram", socials.instagram)} testid="social-instagram" />
          <Row icon={Music2} label="TikTok" value={socials.tiktok} href={socialUrl("tiktok", socials.tiktok)} testid="social-tiktok" />
        </div>

        {canEdit && (ancestor || p.tree_node_id) && (
          <button onClick={removeFromTree} data-testid="remove-from-tree-btn"
            className="w-full mt-8 rounded-xl py-3 text-sm font-medium flex items-center justify-center gap-2"
            style={{ color: "var(--destructive)", background: "var(--overlay)" }}>
            <Trash2 size={15} /> Remove from family tree
          </button>
        )}
      </div>
      <BottomSheet open={showCapsule} onClose={() => setShowCapsule(false)} title="Time Capsule" height="90dvh">
        {showCapsule && <TimeCapsule viewUserId={p.id} viewUserName={p.name} />}
      </BottomSheet>
    </div>
  );
}
